import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Car, MapPin, Clock, Users, ChevronRight, Check, Star, 
  Plane, CalendarClock, Sparkles, ArrowRight, X, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { taxiBooking, taxiCarrier } from '@/lib/api';

/**
 * AirportTransferOffer Component
 * Shows automatic transfer offers after flight/hotel booking
 * 
 * Props:
 * - flightInfo: { arrival_airport, arrival_time, departure_airport, departure_time, flight_number }
 * - hotelInfo: { name, address, city }
 * - customerInfo: { name, phone, email }
 * - onClose: callback when dismissed
 * - isOpen: boolean to control visibility
 */
const AirportTransferOffer = ({ 
  flightInfo, 
  hotelInfo, 
  customerInfo, 
  onClose, 
  isOpen = true 
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'tr';
  
  const [loading, setLoading] = useState(false);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingRequest, setBookingRequest] = useState(null);
  
  // Determine transfer details
  const isArrival = flightInfo?.arrival_airport;
  const airport = isArrival ? flightInfo?.arrival_airport : flightInfo?.departure_airport;
  const transferTime = isArrival ? flightInfo?.arrival_time : flightInfo?.departure_time;
  const destination = hotelInfo?.address || hotelInfo?.name || t('taxi.yourDestination', 'Your destination');
  
  // Estimated prices based on class
  const classBasePrices = {
    economy: 45,
    komfort: 55,
    business: 75,
    vip: 95,
    suv: 85,
    van: 120,
    premium: 130,
    kleinbus: 180,
    autobus: 350
  };

  useEffect(() => {
    if (isOpen) {
      loadVehicleClasses();
    }
  }, [isOpen]);

  const loadVehicleClasses = async () => {
    try {
      const res = await taxiCarrier.getVehicleClasses();
      setVehicleClasses(res.data.vehicle_classes || []);
      // Pre-select economy as default
      if (res.data.vehicle_classes?.length > 0) {
        setSelectedClass('economy');
      }
    } catch (error) {
      console.error('Error loading vehicle classes:', error);
    }
  };

  const handleBookTransfer = async () => {
    if (!selectedClass) {
      toast.error(t('taxi.selectVehicleClass', 'Please select a vehicle class'));
      return;
    }

    setLoading(true);
    try {
      // Create booking request
      const requestData = {
        booking_type: 'transfer',
        pickup_address: isArrival ? airport : destination,
        pickup_datetime: transferTime || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        dropoff_address: isArrival ? destination : airport,
        passengers: 2,
        luggage: 2,
        children: {},
        preferred_vehicle_class: selectedClass,
        flight_number: flightInfo?.flight_number || null,
        return_trip: false,
        comments: `Otomatik transfer teklifi - ${flightInfo?.flight_number || 'Uçuş'} / ${hotelInfo?.name || ''}`,
        contact_name: customerInfo?.name || '',
        contact_phone: customerInfo?.phone || '',
        contact_email: customerInfo?.email || ''
      };

      const res = await taxiBooking.createRequest(requestData);
      setBookingRequest(res.data.booking_request);
      setBookingSuccess(true);
      toast.success(t('taxi.transferRequestCreated', 'Transfer request created!'));
    } catch (error) {
      console.error('Error creating transfer request:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const getClassIcon = (classId) => {
    const icons = {
      economy: '🚗',
      vip: '👑',
      komfort: '🚙',
      suv: '🚜',
      business: '💼',
      van: '🚐',
      premium: '✨',
      kleinbus: '🚌',
      autobus: '🚎'
    };
    return icons[classId] || '🚗';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="airport-transfer-offer-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-orange-600">
            <Sparkles className="w-5 h-5" />
            <DialogTitle>{t('taxi.airportTransferOffer', 'Airport Transfer Offer')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('taxi.transferOfferDesc', 'Complete your journey with a comfortable transfer')}
          </DialogDescription>
        </DialogHeader>

        {bookingSuccess ? (
          <div className="py-6 text-center" data-testid="transfer-booking-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t('taxi.transferRequested', 'Transfer Requested!')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('taxi.driversWillContact', 'Drivers will send you offers shortly.')}
            </p>
            <p className="text-sm bg-gray-100 px-3 py-2 rounded-lg font-mono">
              {bookingRequest?.request_number}
            </p>
            <Button onClick={onClose} className="mt-6 w-full">
              {t('common.close', 'Close')}
            </Button>
          </div>
        ) : (
          <>
            {/* Transfer Route */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <div className="w-0.5 h-12 bg-gray-300" />
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('taxi.pickup', 'Pickup')}</p>
                    <p className="font-medium flex items-center gap-1">
                      {isArrival ? <Plane className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                      {isArrival ? airport : destination}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('taxi.dropoff', 'Drop-off')}</p>
                    <p className="font-medium flex items-center gap-1">
                      {isArrival ? <MapPin className="w-4 h-4" /> : <Plane className="w-4 h-4" />}
                      {isArrival ? destination : airport}
                    </p>
                  </div>
                </div>
              </div>
              {flightInfo?.flight_number && (
                <Badge variant="outline" className="mt-3">
                  <Plane className="w-3 h-3 mr-1" />
                  {flightInfo.flight_number}
                </Badge>
              )}
            </div>

            {/* Vehicle Class Selection */}
            <div className="space-y-3">
              <Label>{t('taxi.selectVehicle', 'Select Vehicle')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {vehicleClasses.slice(0, 6).map((vc) => (
                  <Card
                    key={vc.id}
                    className={`cursor-pointer transition-all p-2 text-center ${
                      selectedClass === vc.id 
                        ? 'ring-2 ring-orange-500 bg-orange-50' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedClass(vc.id)}
                    data-testid={`transfer-vehicle-${vc.id}`}
                  >
                    <div className="text-2xl mb-1">{getClassIcon(vc.id)}</div>
                    <p className="text-xs font-medium">{currentLang === 'tr' ? vc.name_tr : vc.name}</p>
                    <p className="text-xs text-muted-foreground">
                      <Users className="w-3 h-3 inline" /> {vc.max_passengers}
                    </p>
                    <p className="text-sm font-semibold text-orange-600 mt-1">
                      ~€{classBasePrices[vc.id] || 50}
                    </p>
                  </Card>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-4">
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-600" />
                {t('taxi.freeCancellation24h', 'Free cancellation 24h before')}
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-600" />
                {t('taxi.meetAndGreet', 'Meet & greet service')}
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-600" />
                {t('taxi.flightTracking', 'Flight tracking')}
              </span>
            </div>

            <DialogFooter className="mt-4 flex gap-2">
              <Button variant="outline" onClick={onClose}>
                {t('taxi.maybeLater', 'Maybe Later')}
              </Button>
              <Button 
                onClick={handleBookTransfer} 
                disabled={loading || !selectedClass}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="book-transfer-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Car className="w-4 h-4 mr-2" />
                )}
                {t('taxi.bookTransfer', 'Book Transfer')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * AirportTransferBanner Component
 * A compact banner to show at the bottom of booking confirmation pages
 */
export const AirportTransferBanner = ({ 
  airport, 
  destination, 
  customerInfo,
  flightNumber,
  onBookClick 
}) => {
  const { t } = useTranslation();
  
  return (
    <Card className="bg-gradient-to-r from-orange-500 to-amber-500 text-white overflow-hidden" data-testid="airport-transfer-banner">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Car className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-semibold">{t('taxi.needTransfer', 'Need Airport Transfer?')}</h4>
              <p className="text-sm text-white/80">
                {airport} <ArrowRight className="w-3 h-3 inline" /> {destination}
              </p>
            </div>
          </div>
          <Button 
            variant="secondary" 
            onClick={onBookClick}
            className="bg-white text-orange-600 hover:bg-orange-50"
            data-testid="banner-book-transfer-btn"
          >
            {t('taxi.bookNow', 'Book Now')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * SmartTransferSuggestion Component
 * Intelligent suggestion based on booking context
 */
export const SmartTransferSuggestion = ({
  bookingType, // 'flight' | 'hotel'
  bookingDetails,
  onAccept,
  onDismiss
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  
  if (dismissed) return null;
  
  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };
  
  return (
    <div 
      className="fixed bottom-4 right-4 max-w-sm bg-white rounded-xl shadow-2xl border border-orange-200 overflow-hidden z-50 animate-in slide-in-from-bottom-4"
      data-testid="smart-transfer-suggestion"
    >
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-medium text-sm">{t('taxi.smartSuggestion', 'Smart Suggestion')}</span>
        </div>
        <button onClick={handleDismiss} className="hover:bg-white/20 rounded p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-600 mb-3">
          {bookingType === 'flight' 
            ? t('taxi.flightBookedSuggestion', 'You booked a flight! Do you need a transfer to your hotel?')
            : t('taxi.hotelBookedSuggestion', 'You booked a hotel! Do you need airport pickup?')
          }
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDismiss} className="flex-1">
            {t('common.noThanks', 'No, thanks')}
          </Button>
          <Button size="sm" onClick={onAccept} className="flex-1 bg-orange-500 hover:bg-orange-600">
            {t('taxi.yesPlease', 'Yes, please!')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AirportTransferOffer;
